import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import SlidePanel from "./SlidePanel";
import UserLink from "./UserLink";
import { splitSections, knownFacts } from "../helpers/description";
import { ImageCredit } from "./ImageCarousel";

function Gallery({ images, meta }) {
  const [i, setI] = useState(0);
  const [broken, setBroken] = useState({});
  const list = (images || []).filter((_, idx) => !broken[idx]);
  useEffect(() => setI(0), [images]);
  if (!list.length) return null;
  const cur = Math.min(i, list.length - 1);
  return (
    <div className="sd-gallery">
      <img
        src={list[cur]}
        alt=""
        onError={() => setBroken((b) => ({ ...b, [images.indexOf(list[cur])]: true }))}
      />
      {list.length > 1 && (
        <>
          <button className="sd-nav sd-prev" onClick={() => setI((cur - 1 + list.length) % list.length)} aria-label="Previous image">‹</button>
          <button className="sd-nav sd-next" onClick={() => setI((cur + 1) % list.length)} aria-label="Next image">›</button>
          <span className="sd-count">{cur + 1} / {list.length}</span>
        </>
      )}
      <ImageCredit meta={meta?.[list[cur]]} />
    </div>
  );
}

export default function ShipDetails({
  ship, account, isFavorite, favoritePending,
  onClose, onShowOnMap, onToggleFavorite, onShare, onEdit, onReport, onHistory, onOpenUser,
}) {
  const sections = useMemo(() => splitSections(ship.description, ship.title), [ship.description, ship.title]);
  const facts = useMemo(() => knownFacts(ship), [ship]);
  const [open, setOpen] = useState({});
  const [filter, setFilter] = useState("");
  const [copied, setCopied] = useState(false);

  // Long entries start tidy: only the first section is open.
  const long = (ship.description || "").length > 700 && sections.length > 2;
  useEffect(() => {
    setOpen(long ? { [sections[0]?.id]: true } : Object.fromEntries(sections.map((s) => [s.id, true])));
    setFilter("");
  }, [ship.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const q = filter.trim().toLowerCase();
  const matches = (s) => !q || s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q);
  const shown = sections.filter(matches);
  const isOpen = (s) => (q ? true : !!open[s.id]);
  const allOpen = sections.length > 0 && sections.every((s) => open[s.id]);

  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));
  const jump = (id) => {
    setOpen((o) => ({ ...o, [id]: true }));
    setFilter("");
    setTimeout(() => document.getElementById(`sd-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 30);
  };

  const coords = `${Number(ship.lat).toFixed(5)}, ${Number(ship.lng).toFixed(5)}`;
  const copyCoords = async () => {
    try { await navigator.clipboard.writeText(coords); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  return (
    <SlidePanel title="Ship Details" subtitle={ship.title} onClose={onClose} wide xl>
      <Gallery images={ship.images} meta={ship.image_meta} />
      {ship.photo_attribution && <p className="image-credit">Photo attribution: {ship.photo_attribution}</p>}

      <div className="sd-head">
        <h3>{ship.title}</h3>
        <p className="credit-line" style={{ margin: 0 }}>Boat ID: <code>{ship.id}</code></p>
        {ship.submitted_by && (
          <p className="credit-line" style={{ margin: 0 }}>
            Submitted by <UserLink username={ship.submitted_by} onOpen={onOpenUser} />
            {ship.edited_by?.length > 0 && <> · Edits by {ship.edited_by.map((u, i) => <React.Fragment key={u}>{i > 0 && ", "}<UserLink username={u} onOpen={onOpenUser} /></React.Fragment>)}</>}
          </p>
        )}
      </div>

      <div className="sd-actions">
        <button className="panel-primary sd-btn" onClick={onShowOnMap}>📍 Show on map</button>
        {account && (
          <button className={`panel-secondary sd-btn${isFavorite ? " sd-fav-on" : ""}`} disabled={favoritePending} onClick={onToggleFavorite}>
            {isFavorite ? "★ Favorited" : "☆ Favorite"}
          </button>
        )}
        <button className="panel-secondary sd-btn" onClick={onShare}>🔗 Share</button>
        <button className="panel-secondary sd-btn" onClick={onEdit}>✎ Suggest edit</button>
        {onHistory && <button className="panel-secondary sd-btn" onClick={onHistory}>📜 History</button>}
        {onReport && <button className="panel-secondary sd-btn sd-report" onClick={onReport} title="Tell the admins something is wrong with this entry">⚑ Report</button>}
      </div>

      {facts.length > 0 && (
        <section className="sd-card">
          <h4 className="sd-card-title">Quick facts</h4>
          <dl className="sd-facts">
            {facts.map(([label, value]) => (
              <div key={label}><dt>{label}</dt><dd>{value}</dd></div>
            ))}
          </dl>
        </section>
      )}

      <section className="sd-card">
        <h4 className="sd-card-title">Location</h4>
        <div className="sd-coords">
          <code>{coords}</code>
          <button className="panel-link" onClick={copyCoords}>{copied ? "Copied ✓" : "Copy"}</button>
          <a className="panel-link" href={`https://www.google.com/maps?q=${ship.lat},${ship.lng}`} target="_blank" rel="noopener noreferrer">Google Maps ↗</a>
        </div>
      </section>

      {sections.length > 0 && (
        <section className="sd-card">
          <div className="sd-card-row">
            <h4 className="sd-card-title">About this wreck</h4>
            {sections.length > 1 && (
              <button className="panel-link" onClick={() => setOpen(Object.fromEntries(sections.map((s) => [s.id, !allOpen])))}>
                {allOpen ? "Collapse all" : "Expand all"}
              </button>
            )}
          </div>

          {sections.length > 1 && (
            <>
              <div className="sd-toc" aria-label="Jump to section">
                {sections.map((s) => (
                  <button key={s.id} className="sd-chip" onClick={() => jump(s.id)}>{s.title}</button>
                ))}
              </div>
              {(ship.description || "").length > 1200 && (
                <input
                  className="panel-search"
                  type="search"
                  placeholder="Find in this entry…"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                />
              )}
            </>
          )}

          {shown.length === 0 ? (
            <p className="panel-muted">Nothing in this entry matches “{filter}”.</p>
          ) : (
            shown.map((s) => (
              <div key={s.id} id={`sd-${s.id}`} className={`sd-section${isOpen(s) ? " open" : ""}`}>
                <button className="sd-section-head" aria-expanded={isOpen(s)} onClick={() => toggle(s.id)}>
                  <span>{s.title}</span>
                  <span className="sd-caret">▾</span>
                </button>
                {isOpen(s) && (
                  <div className="sd-section-body sd-md">
                    <ReactMarkdown>{s.body}</ReactMarkdown>
                  </div>
                )}
              </div>
            ))
          )}
        </section>
      )}

      {ship.links?.length > 0 && (
        <section className="sd-card">
          <h4 className="sd-card-title">Links</h4>
          <ul className="sd-links">
            {ship.links.map((l, i) => (
              <li key={i}><a href={l} target="_blank" rel="noopener noreferrer">{l.replace(/^https?:\/\/(www\.)?/, "")}</a></li>
            ))}
          </ul>
        </section>
      )}
    </SlidePanel>
  );
}
